import { NavLink, Navigate, Route, Routes } from 'react-router-dom';
import type { Permission } from '@foundation/shared';
import { ROLE_LABEL_TH } from '@foundation/shared';
import { useAuth } from './auth';
import { useBranding } from './lib/branding';
import { Loading } from './components/ui';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import StaffList from './pages/StaffList';
import StaffDetail from './pages/StaffDetail';
import BeneficiaryList from './pages/BeneficiaryList';
import BeneficiaryDetail from './pages/BeneficiaryDetail';
import FormList from './pages/FormList';
import FormEditor from './pages/FormEditor';
import VisitList from './pages/VisitList';
import VisitDetail from './pages/VisitDetail';
import DonationList from './pages/DonationList';
import DonationDetail from './pages/DonationDetail';
import DonationCreate from './pages/DonationCreate';
import DonorList from './pages/DonorList';
import DonorDetail from './pages/DonorDetail';
import CaseList from './pages/CaseList';
import CaseDetail from './pages/CaseDetail';
import CaseCreate from './pages/CaseCreate';
import HelpRequestList from './pages/HelpRequestList';
import Reports from './pages/Reports';
import TaxReport from './pages/TaxReport';
import RichMenus from './pages/RichMenus';
import OrgLifecycle from './pages/OrgLifecycle';
import Settings from './pages/Settings';
import LineChannels from './pages/LineChannels';
import Platform from './pages/Platform';

interface NavItem {
  to: string;
  label: string;
  permission?: Permission;
}

const NAV: NavItem[] = [
  { to: '/', label: 'ภาพรวม' },
  { to: '/beneficiaries', label: 'ผู้รับความช่วยเหลือ', permission: 'beneficiary:read' },
  { to: '/visits', label: 'งานลงพื้นที่', permission: 'visit:read' },
  { to: '/forms', label: 'แบบสำรวจ', permission: 'form:read' },
  { to: '/staff', label: 'จัดการเจ้าหน้าที่', permission: 'staff:read' },
  { to: '/donations', label: 'เงินบริจาค', permission: 'donation:read' },
  { to: '/donors', label: 'ผู้บริจาค', permission: 'donor:read' },
  { to: '/cases', label: 'เคสความช่วยเหลือ', permission: 'case:read' },
  { to: '/help-requests', label: 'คำขอจากประชาชน', permission: 'beneficiary:read' },
  { to: '/reports', label: 'รายงาน', permission: 'report:read' },
  { to: '/tax-report', label: 'ยื่นสรรพากร', permission: 'report:export' },
  { to: '/rich-menus', label: 'เมนู LINE', permission: 'settings:write' },
  { to: '/line-channels', label: 'ช่องทาง LINE', permission: 'settings:write' },
  { to: '/settings', label: 'ตั้งค่าระบบ', permission: 'donation:read' },
  { to: '/org', label: 'การใช้งานและข้อมูล', permission: 'settings:write' },
];

function Shell() {
  const { me, logout, allowed } = useAuth();
  const branding = useBranding();

  return (
    <div className="app-shell">
      <nav className="sidebar">
        <div className="sidebar-brand">
          {branding.logoDataUri && (
            <img className="sidebar-logo" src={branding.logoDataUri} alt="" />
          )}
          <span>{branding.nameTh || 'ระบบบริหารจัดการมูลนิธิ'}</span>
        </div>

        {NAV.filter((n) => !n.permission || allowed(n.permission)).map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.to === '/'}
            className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}
          >
            {n.label}
          </NavLink>
        ))}

        <div className="sidebar-footer">
          <div style={{ fontWeight: 600, color: 'var(--c-text)' }}>{me?.fullName}</div>
          <div>{me ? ROLE_LABEL_TH[me.role] : ''}</div>
          <button className="link" style={{ marginTop: 8 }} onClick={() => void logout()}>
            ออกจากระบบ
          </button>
        </div>
      </nav>

      <main className="content">
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/beneficiaries" element={<BeneficiaryList />} />
          <Route path="/beneficiaries/:id" element={<BeneficiaryDetail />} />
          <Route path="/staff" element={<StaffList />} />
          <Route path="/staff/:id" element={<StaffDetail />} />
          <Route path="/visits" element={<VisitList />} />
          <Route path="/visits/:id" element={<VisitDetail />} />
          <Route path="/forms" element={<FormList />} />
          <Route path="/forms/:id" element={<FormEditor />} />
          <Route path="/donations" element={<DonationList />} />
          <Route path="/donations/new" element={<DonationCreate />} />
          <Route path="/donations/:id" element={<DonationDetail />} />
          <Route path="/donors" element={<DonorList />} />
          <Route path="/donors/:id" element={<DonorDetail />} />
          <Route path="/cases" element={<CaseList />} />
          <Route path="/cases/new" element={<CaseCreate />} />
          <Route path="/cases/:id" element={<CaseDetail />} />
          <Route path="/help-requests" element={<HelpRequestList />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/tax-report" element={<TaxReport />} />
          <Route path="/rich-menus" element={<RichMenus />} />
          <Route path="/line-channels" element={<LineChannels />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/org" element={<OrgLifecycle />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}

/**
 * คอนโซลแพลตฟอร์มอยู่นอกการล็อกอินของเจ้าหน้าที่โดยตั้งใจ
 *
 * มันใช้ตัวตนคนละชุด (platform_user) และ token คนละชนิด ถ้าเอาไปไว้ใต้ <Shell>
 * จะต้องล็อกอินเป็นเจ้าหน้าที่ของมูลนิธิใดมูลนิธิหนึ่งก่อน ซึ่งขัดกับหลักที่ว่า
 * ผู้ดูแลแพลตฟอร์มต้องไม่สังกัดมูลนิธิไหนเลย
 */
export default function App() {
  return (
    <Routes>
      <Route path="/platform/*" element={<Platform />} />
      <Route path="*" element={<StaffArea />} />
    </Routes>
  );
}

function StaffArea() {
  const { me, loading } = useAuth();

  if (loading) return <Loading label="กำลังตรวจสอบสิทธิ์…" />;
  if (!me) return <Login />;
  return <Shell />;
}
